/**
 * @fileoverview Saída do app por CAPABILITY — nunca por marca/wrapper.
 *
 * O `window.close()` que usávamos como única saída é NO-OP na maioria dos alvos:
 * - Tizen: o WebView ignora `close()` de janela que o script não abriu. A única
 *   saída é a API nativa `tizen.application.getCurrentApplication().exit()`.
 * - webOS: a saída canônica é `webOSSystem.platformBack()` (devolve o controle ao
 *   launcher). O `close()` funciona em parte das versões, então fica de fallback.
 * - Android WebView / navegador: `close()` só fecha janela aberta por script →
 *   quase sempre no-op. Nesses alvos NÃO existe saída programática: quem sai é o
 *   usuário (Home do controle / botão do sistema).
 *
 * Consequência prática (o bug que fez o dono desinstalar o app): um botão "Sair"
 * que não sai deixa o usuário preso num gate sem rota de escape. Então esta
 * função é HONESTA — devolve o método que conseguiu usar, e `'unsupported'`
 * quando não há como fechar. A UI usa isso pra mostrar a alternativa real
 * ("use o botão Home") em vez de fingir que saiu.
 *
 * Feature-detection pura sobre globais que o WebView da PLATAFORMA injeta
 * (`tizen`, `webOSSystem`, `webOS`) — não é código de wrapper, roda igual no
 * bundle comum. `win` é injetável pra o teste rodar sem DOM.
 *
 * Duas funções, dois papéis:
 *   - `canExitApp()` PERGUNTA (não executa) — a UI usa pra decidir se um botão
 *     "Sair do app" faz sentido neste alvo. `apps/tv` é UM bundle pra TRÊS
 *     mundos (TV empacotada, web, mobile) e no browser "sair" não existe.
 *   - `exitApp()` EXECUTA e devolve o método que conseguiu usar.
 */

export type ExitMethod = 'tizen' | 'webos' | 'window-close' | 'unsupported'

/**
 * "Existe saída neste alvo?" — a pergunta que a UI precisa fazer ANTES de
 * oferecer um botão "Sair do app", sem executar coisa nenhuma.
 *
 * Por que não dá pra derivar de `exitApp()`: aquela função TENTA sair. E por
 * que `window.close` não serve de sinal: ele existe em TODO navegador, sempre,
 * e é no-op quando a janela não foi aberta por script. Detectar por ele faria o
 * botão aparecer no `app.swipetvplayer.com` — justamente o alvo onde "sair" não
 * existe (quem fecha aba é o usuário).
 *
 * O sinal certo é "sou um app EMPACOTADO":
 *   1. API nativa de saída presente (`tizen`, `webOSSystem`/`webOS`), ou
 *   2. o documento veio do bundle local (`file:`), ou o WebView da LG se
 *      anunciou (`PalmSystem`) — empacotado mesmo que o firmware não injete
 *      `platformBack`. Nesse caso `exitApp()` ainda tenta o `close()` e, se não
 *      fechar, `useAppExit` mostra o caminho honesto ("use o botão Home").
 *
 * É capability/ambiente, nunca marca: nenhum ramo lê UA nem nome de fabricante.
 */
export function canExitApp(
  win: ExitCapableWindow = window as unknown as ExitCapableWindow,
  loc: { protocol?: string } | undefined = typeof location === 'undefined'
    ? undefined
    : location,
): boolean {
  try {
    if (typeof win.tizen?.application?.getCurrentApplication === 'function') return true
    if (typeof (win.webOSSystem?.platformBack ?? win.webOS?.platformBack) === 'function') {
      return true
    }
    if (win.PalmSystem != null) return true
    // `file:` = bundle empacotado (.ipk/.wgt). Web e mobile servem por http(s).
    if (loc?.protocol === 'file:') return true
  } catch {
    // Acesso bloqueado a global nativo = trate como "sem saída". Melhor não
    // oferecer o botão do que oferecer um que não funciona.
  }
  return false
}

interface TizenCurrentApplication {
  exit?: () => void
}

export interface ExitCapableWindow {
  tizen?: {
    application?: {
      getCurrentApplication?: () => TizenCurrentApplication | null | undefined
    }
  }
  /** webOS 3.0+ (injetado pelo WAM). */
  webOSSystem?: { platformBack?: () => void }
  /** webOS legado / webOSTV.js. */
  webOS?: { platformBack?: () => void }
  close?: () => void
  /** Só usado pra detectar que estamos num WebView webOS antigo sem platformBack. */
  PalmSystem?: unknown
}

/**
 * Tenta fechar o app. Devolve o método efetivamente usado — `'unsupported'`
 * quando nenhuma capability de saída existe (a UI PRECISA tratar esse caso).
 *
 * Nunca lança: um erro da API nativa cai no próximo candidato.
 */
export function exitApp(win: ExitCapableWindow = window as unknown as ExitCapableWindow): ExitMethod {
  // 1. Tizen — única saída real; `close()` não fecha o app.
  try {
    const getCurrent = win.tizen?.application?.getCurrentApplication
    if (typeof getCurrent === 'function') {
      const app = getCurrent.call(win.tizen?.application)
      if (app && typeof app.exit === 'function') {
        app.exit()
        return 'tizen'
      }
    }
  } catch {
    // API nativa indisponível/bloqueada — tenta o próximo.
  }

  // 2. webOS — platformBack() devolve o controle ao launcher (saída canônica LG).
  try {
    const platformBack = win.webOSSystem?.platformBack ?? win.webOS?.platformBack
    if (typeof platformBack === 'function') {
      platformBack()
      return 'webos'
    }
  } catch {
    // idem.
  }

  // 3. Fallback genérico — funciona em parte das TVs e em janelas abertas por
  //    script. Em navegador comum é no-op silencioso; por isso o retorno é
  //    'window-close' (tentou) e não uma promessa de que fechou. A UI confirma
  //    o desfecho por timeout (ver useAppExit).
  try {
    if (typeof win.close === 'function') {
      win.close()
      return 'window-close'
    }
  } catch {
    // idem.
  }

  return 'unsupported'
}
